import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { userApi } from "@bw/shared/api";
import type { AgendaItem, PlayerAgendaPage, PlayerAgendaQuery } from "@bw/shared/types/generated";

export interface AgendaWindow { start: string; end: string }

export function usePlayerAgenda(
  mode: NonNullable<PlayerAgendaQuery["mode"]>, limit: number, window?: AgendaWindow,
) {
  const { user } = useAuth();
  const client = useQueryClient();
  const queryKey = ["player-agenda", user?.uid, mode, limit, window?.start, window?.end] as const;
  const query = useInfiniteQuery({
    queryKey,
    enabled: Boolean(user),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => userApi.getMyAgenda({
      mode, limit, cursor: pageParam, start: window?.start, end: window?.end,
    }, signal),
    getNextPageParam: (last: PlayerAgendaPage, pages: PlayerAgendaPage[]) => {
      if (pages.some((page) => page.partial)) return undefined;
      const cursor = last.next_cursor ?? undefined;
      // Do not loop if an invalid upstream page repeats an earlier cursor.
      return cursor && !pages.slice(0, -1).some((page) => page.next_cursor === cursor) ? cursor : undefined;
    },
    refetchOnMount: "always",
  });
  const pages = query.data?.pages ?? [];
  const unavailable = new Set(pages.flatMap((page) => page.sources
    .filter((source) => source.status === "unavailable").map((source) => source.source)));
  const seen = new Set<string>();
  const items: AgendaItem[] = pages.flatMap((page) => page.items).filter((item) => {
    const identity = `${item.key.source}:${item.key.row_id}`;
    if (unavailable.has(item.key.source) || seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
  const current = Boolean(user) && query.isSuccess && query.isFetchedAfterMount &&
    query.fetchStatus === "idle" && query.error === null;
  return {
    ...query,
    items: current ? items : [],
    window: current ? pages[0] : undefined,
    unavailable: [...unavailable],
    partial: pages.some((page) => page.partial) || unavailable.size > 0,
    current,
    restart: () => client.resetQueries({ queryKey, exact: true }),
  };
}
